// The projection LOAD hook — fetch a surface's typed projection from the injected transport, tracking the
// honest load lifecycle (loading → loaded | error) and exposing a `reload` the surface calls after routing a
// write intent (so it re-reads the state the OWNING module recorded — G-182, never an optimistic client
// guess). No domain logic here: it delegates the read to the SurfaceApi and holds only the async view-state.
//
// The `error` state renders an honest failure (P-SELF) with NO internal detail (G-197) — the surface shows
// "couldn't load" copy, never a stack/error code. In a UI test the injected fake resolves synchronously, so
// the projection is available on the first committed render.

import { useCallback, useEffect, useRef, useState } from 'react';

/** The async view-state of a projection load. */
export interface ProjectionState<T> {
  readonly data: T | null;
  readonly loading: boolean;
  readonly error: boolean;
  /** The HTTP status the OWNING read refused with, when the failure carried one (the transport embeds it in
   *  the thrown message — api-client `read failed (<status>)`), else null for a transient/opaque failure. It
   *  lets the shared SurfaceFrame tell a PERMANENT role/permission refusal (403) from a transient outage
   *  WITHOUT widening what a surface renders — it is a render-honesty signal, never a security boundary (the
   *  owning read is the boundary; G-180). */
  readonly errorStatus: number | null;
  /** Re-run the load (called after a routed write intent so the surface re-reads the recorded state). */
  readonly reload: () => void;
}

/** Read the HTTP status the transport embedded in a failed read's message (api-client throws
 *  `read failed (<status>)`), or null when the failure carried no status (a transient/opaque error). */
function readErrorStatus(error: unknown): number | null {
  const message = error instanceof Error ? error.message : String(error);
  const match = /\((\d{3})\)/.exec(message);
  return match !== null ? Number(match[1]) : null;
}

/** Load a projection via the injected transport. `load` is the SurfaceApi method (memoise at the call site). */
export function useProjection<T>(load: () => Promise<T>): ProjectionState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [errorStatus, setErrorStatus] = useState<number | null>(null);
  const [nonce, setNonce] = useState(0);
  // The read this hook last STARTED. A different `load` is a DIFFERENT read (another surface, id or filter)
  // and must show the loading placeholder; an explicit `reload()` re-reads the SAME read and must not.
  const startedLoad = useRef<(() => Promise<T>) | null>(null);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    let live = true;
    // A RELOAD keeps the last good projection MOUNTED while the re-read is in flight. Blanking the surface
    // to the "Loading…" placeholder unmounted the loaded body, and with it the live component state hanging
    // off that body: on the Task modal that is the run preview and its open stream subscription, which the
    // composer had just opened for the accepted send. So every routed write made the modal blink and dropped
    // the thinking affordance for a frame before the re-read landed (Bug 40). A read that has never produced
    // data still shows the placeholder, and a re-read that fails still fails honestly.
    const isReload = startedLoad.current === load;
    startedLoad.current = load;
    if (!isReload) setLoading(true);
    setError(false);
    setErrorStatus(null);
    load()
      .then((result) => {
        if (!live) return;
        setData(result);
        setLoading(false);
      })
      .catch((cause: unknown) => {
        if (!live) return;
        // Fail honestly — no fabricated projection, no internal detail surfaced (P-SELF, G-197). The status
        // (if the transport embedded one) only lets the frame pick honest role vs transient copy — it never
        // widens what renders.
        setError(true);
        setErrorStatus(readErrorStatus(cause));
        setLoading(false);
      });
    return () => {
      live = false;
    };
    // `load` is a stable SurfaceApi method (memoised at the call site); `nonce` drives an explicit reload.
  }, [load, nonce]);

  return { data, loading, error, errorStatus, reload };
}
